import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, Sparkles, CalendarCheck } from 'lucide-react';
import MarketingPage, { ContentCard } from '@/components/marketing/MarketingPage';
import { pageJsonLd, SITE } from '@/components/shared/Seo';

const CONTACT_JSONLD = pageJsonLd({
  type: 'ContactPage',
  name: 'Contact Numuv',
  description: 'Book a live Numuv demo, ask a question, or share a partnership idea. The team replies within one business day.',
  path: '/contact-us',
  breadcrumbs: [{ name: 'Contact us', path: '/contact-us' }],
  extra: [{
    '@type': 'ContactPoint',
    contactType: 'sales and support',
    email: 'hello@numuv.app',
    url: `${SITE}/contact-us`,
    availableLanguage: ['English'],
  }],
});

const API = process.env.REACT_APP_BACKEND_URL;
const INTERESTS = [
  { value: 'hiring', label: 'Hiring Organization' },
  { value: 'staffing', label: 'Staffing Organization' },
  { value: 'applicant', label: 'Applicant' },
  { value: 'partnership', label: 'Partnership / Other' },
];

export default function ContactUsPublic() {
  const [form, setForm] = useState({ name: '', email: '', company: '', interest: 'hiring', message: '' });
  const [mode, setMode] = useState('query'); // 'demo' | 'query'
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, message: `[${mode.toUpperCase()}] ${form.message}` };
      await axios.post(`${API}/api/demo-requests`, payload);
      toast.success(mode === 'demo' ? 'Thanks! Our team will book time shortly.' : 'Thanks! We\'ll get back to you at hello@numuv.app.');
      setForm({ name: '', email: '', company: '', interest: 'hiring', message: '' });
    } catch {
      toast.error('Something went wrong. Please email hello@numuv.app.');
    } finally { setLoading(false); }
  };

  return (
    <MarketingPage
      jsonLd={CONTACT_JSONLD}
      seoTitle="Contact Numuv — book a demo or get in touch"
      seoDescription="Reach the Numuv team at hello@numuv.app, book a live product demo, or share partnership ideas. We reply within one business day."
      seoPath="/contact-us"
      seoKeywords="contact Numuv, book a demo, hello@numuv.app, hiring platform support"
      eyebrow="Get in touch"
      title="Talk to a human at Numuv"
      subtitle="Whether you want a live demo, have a partnership idea, or just a question — we're a quick email away."
    >
      <div className="grid md:grid-cols-3 gap-5 md:gap-7 mt-2">
        <ContentCard className="md:col-span-2 !p-6 md:!p-8" id="form">
          <div className="flex gap-2 mb-5">
            {[
              { key: 'query', label: 'General query', icon: Send },
              { key: 'demo', label: 'Book a demo', icon: CalendarCheck },
            ].map((m) => (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-medium transition ${mode === m.key ? 'aurora-btn text-white' : 'card thm t2'}`}
                data-testid={`contact-mode-${m.key}`}
              >
                <m.icon className="w-3.5 h-3.5" /> {m.label}
              </button>
            ))}
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Your name" required>
                <input required value={form.name} onChange={set('name')} data-testid="contact-name"
                  className="w-full rounded-lg border brd bg-transparent px-3 py-2.5 text-[14px] t1 outline-none focus:border-[#6D5EF7]" />
              </Field>
              <Field label="Email" required>
                <input required type="email" value={form.email} onChange={set('email')} data-testid="contact-email-field"
                  className="w-full rounded-lg border brd bg-transparent px-3 py-2.5 text-[14px] t1 outline-none focus:border-[#6D5EF7]" />
              </Field>
              <Field label="Company">
                <input value={form.company} onChange={set('company')} data-testid="contact-company"
                  className="w-full rounded-lg border brd bg-transparent px-3 py-2.5 text-[14px] t1 outline-none focus:border-[#6D5EF7]" />
              </Field>
              <Field label="I'm interested as">
                <select value={form.interest} onChange={set('interest')} data-testid="contact-interest"
                  className="w-full rounded-lg border brd bg-[color:var(--surface)] px-3 py-2.5 text-[14px] t1 outline-none focus:border-[#6D5EF7]">
                  {INTERESTS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label={mode === 'demo' ? 'What would you like to see in the demo?' : 'How can we help?'} required>
              <textarea required rows={4} value={form.message} onChange={set('message')} data-testid="contact-message"
                className="w-full rounded-lg border brd bg-transparent px-3 py-2.5 text-[14px] t1 outline-none focus:border-[#6D5EF7]" />
            </Field>
            <button type="submit" disabled={loading} className="inline-flex items-center gap-2 rounded-full aurora-btn px-6 py-3 text-[14px] font-semibold text-white shadow-[0_16px_40px_rgba(109,94,247,.38)] disabled:opacity-60 transition" data-testid="contact-submit">
              <Send className="w-3.5 h-3.5" /> {loading ? 'Sending…' : (mode === 'demo' ? 'Request demo' : 'Send message')}
            </button>
          </form>
        </ContentCard>

        <div className="space-y-4">
          <ContentCard className="!p-6">
            <span className="grid h-11 w-11 place-items-center rounded-[13px] bg-[#6D5EF7]/12 text-[#6D5EF7]"><Mail className="w-5 h-5" /></span>
            <p className="dis mt-4 text-[16px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>Email us directly</p>
            <a href="mailto:hello@numuv.app" className="mt-1.5 block text-[14px] text-[#6D5EF7] underline underline-offset-4">hello@numuv.app</a>
            <p className="mt-3 text-[13px] t3 leading-[19px]">We usually reply within one business day.</p>
          </ContentCard>
          <ContentCard className="!p-6">
            <span className="grid h-11 w-11 place-items-center rounded-[13px] bg-[#12B6A6]/12 text-[#12B6A6]"><Sparkles className="w-5 h-5" /></span>
            <p className="dis mt-4 text-[16px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>Support & tickets</p>
            <p className="mt-1.5 text-[13px] t3 leading-[19px]">Existing customer? Sign in and open a support ticket from your dashboard — our agents pick up 24/7.</p>
          </ContentCard>
        </div>
      </div>
    </MarketingPage>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[12.5px] font-medium t2 mb-1.5">{label}{required && <span className="text-[#F43F5E]"> *</span>}</span>
      {children}
    </label>
  );
}
